/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, Fragment } from 'react';
import cx from 'classnames';
import { View, StyleSheet } from 'react-native';

import { showError } from '../App/AppAlert';
import FileUpload, { Response } from '../../support/FileUpload';
import { UPLOAD_MAX_SIZE } from '../../support/shared/constants/Constants';
import { showDialog } from '../App/AppDialog';

import FilePickerButton from '../uiold/FilePickerButton';
import { Button, Card, Text, Link } from '../uiold';

import { ImageType, Market } from '../../types/shared/Market';
import { ImageTypes } from '../../core/constants/MimeTypes';

type Props = {
  market: Market;
  onBeginEdit: ((market: Market) => any) | null | undefined;
  canEdit?: boolean | null | undefined;
  onImageChange: (imageId: string, imageType: ImageType) => void;
  onImageRemove: () => void;
};

type State = {
  upload: FileUpload | null | undefined;
  uploadPreview: string | null | undefined;
  uploadProgress: number;
  dragHover: boolean;
};

const imageTypes = Object.keys(ImageTypes).join(', ');

class MapMarketItem extends Component<Props, State> {
  state: State = {
    upload: null,
    uploadPreview: null,
    uploadProgress: 0,
    dragHover: false,
  };

  componentWillUnmount() {
    if (this.state.upload) {
      this.state.upload.abort();
    }
  }

  render() {
    let { market, canEdit } = this.props;
    let detailsPane;
    if (market.map) {
      detailsPane = (
        <Fragment>
          {canEdit ? (
            <Text style={styles.detailsText}>
              {t(
                'This market has {numSpots} vendor spots. Please mark the location by adding markers to the map.',
                { numSpots: market.num_spots },
              )}
            </Text>
          ) : (
            <Text style={styles.detailsText}>
              {t('This market has {numSpots} vendor spots.', {
                numSpots: market.num_spots,
              })}
            </Text>
          )}

          {canEdit && (
            <>
              <FilePickerButton
                accept={imageTypes}
                onFileSelect={this._onFileSelect}
                title={t('Change Map Image')}
              />
              <View style={styles.padLeft}>
                <Link onPress={this._removeImageButtonClick}>
                  <Text type="label">{t('Remove Map Image')}</Text>
                </Link>
                <View style={styles.linkWrapper}>
                  <Link onPress={this._editMarkersButtonClick}>
                    <Text type="label">{t('Add/Edit Markers')}</Text>
                  </Link>
                </View>
              </View>
            </>
          )}
        </Fragment>
      );
    } else {
      detailsPane = (
        <Text style={styles.detailsText}>
          {t(
            'This market has no Map. To add one, click the square on the left ordrag an image onto it.',
          )}
        </Text>
      );
    }

    return (
      <Card style={styles.card}>
        <Card.Header title={market.name} />
        <View style={styles.rowFlexed}>
          <View>
            {this.state.upload
              ? this._renderProgress()
              : this._renderThumbnail()}
          </View>
          <View style={styles.details}>{detailsPane}</View>
        </View>
      </Card>
    );
  }

  _renderThumbnail = () => {
    let market = this.props.market;
    let isEmpty = !market.map;
    let style;
    let caption;
    if (!market.map) {
      style = {};
    } else {
      style = {
        backgroundImage: `url(/api/images/${market.map.id}/image.${market.map.imageType})`,
      };
    }
    let className = cx({
      'map-thumbnail': true,
      empty: isEmpty,
      'drag-hover': this.state.dragHover,
    });
    if (this.state.dragHover) {
      caption = t('Drop Image File Here');
    } else if (isEmpty) {
      caption = t('Click to Add Map Image');
    } else {
      caption = t('Click to Add/Edit Markers');
    }
    let button = isEmpty ? (
      <FilePickerButton
        style={styles.fillButton}
        onFileSelect={this._onFileSelect}
      />
    ) : (
      <Button
        title=""
        style={styles.fillButton}
        onPress={this._editMarkersButtonClick}
      />
    );
    return (
      <div
        style={style}
        className={className}
        onDragOver={this._onDragOver}
        onDragLeave={this._onDragLeave}
        onDrop={this._onDrop}
      >
        <span className="caption">{caption}</span>
        {button}
      </div>
    );
  };

  _renderProgress = () => {
    let { uploadPreview } = this.state;
    let percent = Math.round(this.state.uploadProgress * 100);
    let style = null;
    if (uploadPreview) {
      style = {
        backgroundImage: `url("${uploadPreview}")`,
      };
    }

    return (
      <div className="map-thumbnail uploading" style={style as any}>
        <div className="caption">{t('Uploading...')}</div>
        <div className="progress">
          <div className="progress-bar" style={{ width: percent + '%' }}>
            <span className="sr-only">
              {t('{percent}% Uploaded', { percent })}
            </span>
          </div>
        </div>
      </div>
    );
  };

  _onDragOver = (event: { [key: string]: any }) => {
    if (hasType(event.dataTransfer, 'Files')) {
      event.stopPropagation();
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      this.setState({ dragHover: true });
    }
  };

  _onDragLeave = (event: { [key: string]: any }) => {
    if (hasType(event.dataTransfer, 'Files')) {
      event.stopPropagation();
      event.preventDefault();
      this.setState({ dragHover: false });
    }
  };

  _onDrop = (event: { [key: string]: any }) => {
    if (hasType(event.dataTransfer, 'Files')) {
      event.stopPropagation();
      event.preventDefault();
      let file = event.dataTransfer.files[0];
      this._attemptImageUpload(file);
      this.setState({ dragHover: false });
    }
  };

  _onFileSelect = (files: Array<File>) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let file = files[0]!;
    this._attemptImageUpload(file);
  };

  _attemptImageUpload = (file: File) => {
    let errorMessage;
    if (!(file.type in ImageTypes)) {
      errorMessage = t('Error: Invalid file type. Please select an image.');
      showError(errorMessage);
      return;
    }
    if (file.size > UPLOAD_MAX_SIZE) {
      errorMessage = t('Error: File exceeds maximum size.');
      showError(errorMessage);
      return;
    }
    let upload = new FileUpload('/api/images', file);
    upload.addListener('progress', this._onUploadProgress);
    upload.addListener('complete', this._onUploadComplete);
    this.setState({
      upload: upload,
      uploadPreview: URL.createObjectURL(file),
      uploadProgress: 0,
    });
    upload.send();
  };

  _onUploadProgress = (loaded: number, total: number) => {
    let progress = loaded / total;
    this.setState({ uploadProgress: progress });
  };

  _onUploadComplete = (error: Error | null, response: Response) => {
    if (error) {
      this._showError(t('Network request failed'), error);
    } else if (response.status !== 200) {
      this._showError(
        t('Upload failed; HTTP Status {responseStatus}', {
          responseStatus: response.status,
        }),
      );
    } else {
      const data = response.body ?? {};
      const imageId = String(data.id);
      const imageType = String(data.imageType);
      this.props.onImageChange(imageId, imageType as ImageType);
    }
    this.setState({ upload: null, uploadPreview: null });
  };

  _showError = (message: string, error?: Error | null | undefined) => {
    showError(t('Error: {message}', { message }));
    logError(message, error);
  };

  _editMarkersButtonClick = () => {
    this.props.onBeginEdit?.(this.props.market);
  };

  _removeImageButtonClick = () => {
    const OK = t('OK');
    showDialog(
      t('Remove map image?'),
      t(
        'Press OK to remove this map image for ${marketName}. You will still need to save your changes to take effect.',
        { marketName: this.props.market.name },
      ),
      [t('Cancel'), OK],
      (value) => {
        if (value === OK) {
          this.props.onImageRemove();
        }
      },
    );
  };
}

function hasType(dataTransfer: DataTransfer, type: string) {
  let types = Array.isArray(dataTransfer.types)
    ? dataTransfer.types
    : Array.from(dataTransfer.types || []);
  return types.indexOf(type) !== -1;
}

function logError(...args: any) {
  console.error(...args); // eslint-disable-line no-console
}

let styles = StyleSheet.create({
  card: {
    marginBottom: 20,
  },
  rowFlexed: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 10,
    paddingHorizontal: 20,
  },
  details: {
    alignItems: 'flex-start',
    flex: 1,
    paddingLeft: 20,
  },
  detailsText: {
    marginBottom: 20,
  },
  padLeft: {
    paddingLeft: 12,
  },
  linkWrapper: {
    marginTop: 7,
  },
  fillButton: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 0,
    boxShadow: 'none',
    // Need to specify vertical and horizontal to override the defaults.
    marginVertical: 0,
    marginHorizontal: 0,
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

export default MapMarketItem;
